import React, { useEffect } from 'react';

import classNames from 'classnames';
import ImagesIcon from 'icons/ic-images.svg';
import InfoIcon from 'icons/info.svg';
import LoadingIcon from 'icons/loading.svg';
import { enableAllPlugins, produce } from 'immer';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import CircleQuestion from 'components/application/CircleQuestion';
import ErrorMessage from 'components/application/ErrorMessage';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import RadioField from 'components/application/RadioField';
import Switch from 'components/application/Switch';
import TextField from 'components/application/TextField';
import Tooltip from 'components/application/Tooltip';

import PersonnelCustomFieldsForm from 'components/personnel/PersonnelCustomFieldsForm';
import PreviewCroppedProfilePhoto from 'components/personnel/PreviewCroppedProfilePhoto';
import SelectCompanyRoles from 'components/personnel/SelectCompanyRoles';
import SelectDivision from 'components/personnel/SelectDivision';
import SelectedTeamsList from 'components/personnel/SelectedTeamsList';
import SelectLineManager from 'components/personnel/SelectLineManager';
import SelectSubcontractor from 'components/personnel/SelectSubcontractor';
import SelectTeams from 'components/personnel/SelectTeams';
import UserCompanyRolesList from 'components/personnel/UserCompanyRolesList';

import { findAssignedCompany } from 'components/helpers/companies';
import { resourceShape } from 'components/helpers/serialisableResources';

enableAllPlugins();

export default function PersonnelForm({
  assignableCompanies,
  assignableRoles,
  currentPersonnel,
  currentSubcontractor,
  domainLineManager,
  domainProfilePhoto,
  domainTeams,
  hasTeamPersonnelEditableAccess,
  isCreatableCompanyRole,
  isEditableProfileAccess,
  isProfilePhotoLoading,
  isSubcontractor,
  onAssignableRoleAdd,
  onCompanyRolesOptionChange,
  onDateFieldChange,
  onDivisionOptionChange,
  onFieldOptionChange,
  onNewCompanyRoleAdd,
  onPersonnelFieldChange,
  onPersonnelInputChange,
  onPersonnelOptionChange,
  onPhotoModalOpen,
  onRemoveRole,
  onSelectPrimary,
  onSubcontractorSelect,
  onTeamSelect,
  onTeamSelectionDelete,
  onTeamSelectionRoleChange,
  onToggle,
  personnel,
  removeErrorStyling,
  requestError,
  setCurrentPersonnel,
  setIsSubcontractor,
  sidePanelContext,
  sidePanelFieldRef,
  user,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const previewablePhotoArea = {
    x: currentPersonnel.profilePhoto?.croppedAreaPercentageX,
    y: currentPersonnel.profilePhoto?.croppedAreaPercentageY,
    width: currentPersonnel.profilePhoto?.croppedAreaPercentageWidth,
    height: currentPersonnel.profilePhoto?.croppedAreaPercentageHeight,
  };

  const previewablePhotoPath = domainProfilePhoto?.links?.originalUrl;
  const previewablePhotoRotation = currentPersonnel.profilePhoto?.rotation;

  const previewablePhotoSize = {
    height: currentPersonnel.profilePhoto?.mediaHeight,
    width: currentPersonnel.profilePhoto?.mediaWidth,
  };

  const assignedDivision = findAssignedCompany({
    assignableCompanies,
    companyId: currentPersonnel.company.companyId,
  });

  const assignedSubcontractor = findAssignedCompany({
    assignableCompanies,
    subcontractorId: currentPersonnel.company.subcontractorId,
  });

  const primaryDivision = assignableCompanies.find(
    (company) =>
      company.attributes.resourceType === 'Company' &&
      company.attributes.primary,
  );

  const subcontractors = assignableCompanies.filter(
    (company) => company.attributes.resourceType === 'Subcontractor',
  );

  const companies = assignableCompanies.filter(
    (company) => company.attributes.resourceType === 'Company',
  );

  const focusableFields = ['firstName', 'lastName', 'externalId', 'email'];

  const firstEmptyField = focusableFields.find(
    (field) => !currentPersonnel[field],
  );

  const isPersonnelUserPresent = user?.attributes.accessType === 'personnel';
  const isAppUserPresent = user && user.attributes.accessType !== 'personnel';

  const isDivisionTeamWarningVisible =
    !!personnel?.relationships?.teams &&
    personnel.relationships.teams.data.length > 0 &&
    currentPersonnel.company.companyId !==
      personnel.relationships.company.data.id;

  const isSubcontractorProfileWarningVisible =
    (!user || isPersonnelUserPresent) && currentPersonnel.profileAccessEnabled;

  const isEmailUsedForLogInPurposesWarningVisible =
    isPersonnelUserPresent &&
    currentPersonnel.email !== personnel.attributes.email;

  const isProfileAccessToggleVisible =
    !isSubcontractor && isEditableProfileAccess;

  let profileAccessToggleDisabledReason;
  if (isProfileAccessToggleVisible) {
    if (isAppUserPresent) {
      profileAccessToggleDisabledReason = 'appUserPresent';
    } else if (
      !currentPersonnel.profileAccessEnabled &&
      currentPersonnel.email.trim().length === 0
    ) {
      profileAccessToggleDisabledReason = 'emailNotPresent';
    }
  }

  useEffect(() => {
    if (sidePanelContext === 'new') {
      setCurrentPersonnel({
        ...currentPersonnel,
        companyRoleIds: [],
        lineManagerId: '',
      });
    }
  }, [currentPersonnel.company.companyId]);

  useEffect(() => {
    if (
      sidePanelContext === 'edit' &&
      currentPersonnel.company.subcontractorId
    ) {
      setIsSubcontractor(true);
    }
  }, []);

  const formattedValidationErrors =
    requestError.validationErrors.email ?
      produce(requestError.validationErrors, (draftValidationErrors) => {
        draftValidationErrors.email.error.detail = `${draftValidationErrors.email.error.detail}. ${i18nT('email.error')}`;
      })
    : requestError.validationErrors;

  const isProfileAccessToggleDisabled = !!profileAccessToggleDisabledReason;

  return (
    <div className='popup__body-form popup__body-form--no-side-padding'>
      <div className='form-container'>
        <div className='form-container-section'>
          <ErrorMessage
            isFallback={requestError.isFallback}
            validationErrors={formattedValidationErrors}
            withSeparateNestedErrors={false}
            wrapperClassName='form-container'
          />
        </div>
        <div className='form-container-section'>
          <TextField
            fieldError={requestError.validationErrors.firstName}
            inputRef={
              firstEmptyField === 'firstName' ? sidePanelFieldRef : null
            }
            isRequired={true}
            label={i18nTCommon('shared.firstName')}
            name='firstName'
            onChange={onPersonnelInputChange}
            removeErrorStyling={removeErrorStyling}
            value={currentPersonnel.firstName}
          />
          <TextField
            fieldError={requestError.validationErrors.lastName}
            inputRef={firstEmptyField === 'lastName' ? sidePanelFieldRef : null}
            isRequired={true}
            label={i18nTCommon('shared.lastName')}
            name='lastName'
            onChange={onPersonnelInputChange}
            removeErrorStyling={removeErrorStyling}
            value={currentPersonnel.lastName}
          />
          <TextField
            fieldError={requestError.validationErrors.externalId}
            inputRef={
              firstEmptyField === 'externalId' ? sidePanelFieldRef : null
            }
            label={i18nTCommon('personnel.personnelId')}
            name='externalId'
            onChange={onPersonnelInputChange}
            removeErrorStyling={removeErrorStyling}
            value={currentPersonnel.externalId}
          />
          <div className='tooltip-parent'>
            <TextField
              fieldError={requestError.validationErrors.email}
              inputRef={firstEmptyField === 'email' ? sidePanelFieldRef : null}
              isDisabled={isAppUserPresent}
              label={i18nTCommon('shared.emailAddress')}
              name='email'
              onChange={(event) => {
                if (!isAppUserPresent) onPersonnelInputChange(event);
              }}
              removeErrorStyling={removeErrorStyling}
              value={currentPersonnel.email}
            />
            {isAppUserPresent && (
              <Tooltip
                className='tooltip-dark--max-w-none tw-top-6'
                placement='top'
                tooltip={
                  <p className='tw-m-0'>
                    <Trans
                      i18nKey='personnel.PersonnelForm.email.tooltip'
                      ns='components'
                    />
                  </p>
                }
                trigger='hover'
              />
            )}
          </div>
          {isEmailUsedForLogInPurposesWarningVisible && (
            <div className='-tw-mt-3 tw-flex tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
              <div className='tw-mr-3 tw-flex tw-h-5 tw-w-4 tw-items-center'>
                <InfoIcon
                  className='[&_path]:tw-fill-amber-800'
                  height={16}
                  width={16}
                />
              </div>
              {i18nT('email.note')}
            </div>
          )}
        </div>

        <div className='tw-flex'>
          <div className='tw-ml-5 tw-mr-4'>
            {(() => {
              if (isProfilePhotoLoading) {
                return (
                  <div className='tw-relative tw-h-16 tw-w-16'>
                    <div className='tw-apply-loading-spinner--blue-light tw-flex tw-items-center tw-justify-center'>
                      <LoadingIcon className='animated-spinner tw-absolute tw-top-0 tw-h-16' />
                    </div>
                    <div className='tw-absolute tw-top-0 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-opacity-50'>
                      <ImagesIcon className='[&_path]:tw-fill-grey-500' />
                    </div>
                  </div>
                );
              }

              if (
                !!previewablePhotoPath &&
                !!previewablePhotoSize.width &&
                (!!previewablePhotoArea?.x || !!previewablePhotoArea?.y)
              ) {
                return (
                  <PreviewCroppedProfilePhoto
                    croppedArea={previewablePhotoArea}
                    imagePath={previewablePhotoPath}
                    mediaSize={previewablePhotoSize}
                    rotation={previewablePhotoRotation}
                  />
                );
              }

              return (
                <ProfilePhotoDisplay
                  componentSize='md'
                  photoUrl={domainProfilePhoto?.links?.croppedUrl}
                  profileInitials={`${currentPersonnel.firstName[0]}${currentPersonnel.lastName[0]}`}
                />
              );
            })()}
          </div>
          <div className='tw-mb-1 tw-mt-2'>
            <a
              className={classNames(
                'tw-cursor-pointer tw-font-medium',
                isProfilePhotoLoading ? 'tw-text-grey-400' : 'tw-text-blue-500',
              )}
              onClick={() => {
                if (!isProfilePhotoLoading) onPhotoModalOpen();
              }}
            >
              {(
                domainProfilePhoto?.links?.croppedUrl ||
                previewablePhotoPath ||
                isProfilePhotoLoading
              ) ?
                i18nTCommon('personnel.editPhoto')
              : i18nTCommon('personnel.addPhoto')}
            </a>
            <p className='tw-mb-2 tw-mt-1 tw-text-grey-500'>
              {i18nT('photo.description')}
            </p>
          </div>
        </div>

        <hr className='tw-my-8 tw-h-px tw-border-0 tw-bg-grey-100' />

        <div className='form-container-section'>
          <label className='collection-select__label tw-mb-6 tw-mt-8 tw-font-medium'>
            {i18nT('personnelType.label')}
          </label>
          <div className='side-panel__radio-buttons-container'>
            <RadioField
              checked={!isSubcontractor}
              label={i18nTCommon('shared.employee')}
              name='personnelType'
              onChange={() => setIsSubcontractor(false)}
              value='employee'
            />
            {!isSubcontractor && companies.length > 1 && (
              <>
                <SelectDivision
                  assignedDivision={assignedDivision}
                  collectionSelectProps={{ customMargin: 'tw-mb-0' }}
                  companies={companies}
                  label={i18nTCommon('shared.division')}
                  onChange={onDivisionOptionChange}
                  sidePanelContext={sidePanelContext}
                />
                {isDivisionTeamWarningVisible && (
                  <div className='tw-ml-7.5 tw-mt-4 tw-flex tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
                    <div className='tw-mr-3 tw-flex tw-h-5 tw-w-4 tw-items-center'>
                      <InfoIcon
                        className='[&_path]:tw-fill-amber-800'
                        height={16}
                        width={16}
                      />
                    </div>
                    {i18nT('personnelType.description')}
                  </div>
                )}
              </>
            )}
            {isProfileAccessToggleVisible && (
              <div
                className='tw-ml-7.5 tw-mt-4 tw-flex tw-cursor-pointer tw-justify-between'
                data-element-name='profile-access-toggle'
                onClick={() => {
                  if (!isProfileAccessToggleDisabled) {
                    onToggle('profileAccessEnabled');
                  }
                }}
              >
                <div className='tw-flex'>
                  <span className='tw-mr-2'>
                    {i18nT('profileAccessEnabled.label')}
                  </span>
                  <Tooltip
                    placement='top'
                    tooltip={i18nT('profileAccessEnabled.tooltip')}
                    trigger='hover'
                  >
                    <CircleQuestion />
                  </Tooltip>
                </div>
                <div
                  className={classNames(
                    'tooltip-parent',
                    isProfileAccessToggleDisabled &&
                      'tw-cursor-not-allowed tw-opacity-30',
                  )}
                >
                  <Switch
                    className='app-switch--md tw-float-right'
                    isChecked={
                      isAppUserPresent || currentPersonnel.profileAccessEnabled
                    }
                    isDisabled={isProfileAccessToggleDisabled}
                  />
                  {isProfileAccessToggleDisabled && (
                    <Tooltip
                      placement='top'
                      tooltip={
                        <p className='tw-m-0'>
                          <Trans
                            i18nKey={`personnel.PersonnelForm.profileAccessEnabled.disabledTooltip.${profileAccessToggleDisabledReason}`}
                            ns='components'
                          />
                        </p>
                      }
                      trigger='hover'
                    />
                  )}
                </div>
              </div>
            )}
            <RadioField
              checked={isSubcontractor}
              label={i18nTCommon('shared.subcontractor')}
              name='personnelType'
              onChange={() => setIsSubcontractor(true)}
              value='subcontractor'
            />
            {isSubcontractor && (
              <>
                {isSubcontractorProfileWarningVisible && (
                  <div className='tw-mb-4 tw-ml-7.5 tw-flex tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-p-3 tw-text-amber-800'>
                    <div className='tw-mr-3 tw-flex tw-h-5 tw-w-4 tw-items-center'>
                      <InfoIcon
                        className='[&_path]:tw-fill-amber-800'
                        height={16}
                        width={16}
                      />
                    </div>
                    {i18nT('personnelType.radioFields.subcontractor.warning')}
                  </div>
                )}
                <SelectSubcontractor
                  assignedSubcontractor={assignedSubcontractor}
                  fieldError={requestError.validationErrors.name}
                  isSubcontractor={isSubcontractor}
                  label={i18nT(
                    'personnelType.radioFields.subcontractor.select.label',
                  )}
                  onChange={onPersonnelOptionChange}
                  onSubcontractorSelect={onSubcontractorSelect}
                  primaryDivision={primaryDivision}
                  removeErrorStyling={removeErrorStyling}
                  sidePanelContext={sidePanelContext}
                  subcontractor={currentSubcontractor}
                  subcontractors={subcontractors}
                />
              </>
            )}
          </div>
          <SelectLineManager
            currentPersonnel={currentPersonnel}
            domainLineManager={domainLineManager}
            isDisabled={isSubcontractor}
            onChange={onPersonnelOptionChange}
          />
        </div>
        <hr className='tw-mt-8 tw-h-px tw-border-0 tw-bg-grey-100' />
        <div className='form-container-section'>
          <label className='collection-select__label tw-mt-8 tw-font-medium'>
            {i18nTCommon('shared.roles')}
          </label>
          <SelectCompanyRoles
            assignableRolesCollection={assignableRoles}
            currentPersonnel={currentPersonnel}
            fieldError={requestError.validationErrors.base}
            isCreatable={isCreatableCompanyRole}
            onAssignableRoleAdd={onAssignableRoleAdd}
            onChange={onCompanyRolesOptionChange}
            onNewCompanyRoleAdd={onNewCompanyRoleAdd}
            primaryDivisionId={primaryDivision.id}
          />
          <UserCompanyRolesList
            assignableRolesCollection={assignableRoles}
            currentPersonnel={currentPersonnel}
            onRemoveRole={onRemoveRole}
            onSelectPrimary={onSelectPrimary}
          />
        </div>
        {hasTeamPersonnelEditableAccess && (
          <>
            <hr className='tw-mt-8 tw-h-px tw-border-0 tw-bg-grey-100' />
            <div className='form-container-section'>
              <SelectTeams
                currentPersonnel={currentPersonnel}
                onTeamSelect={onTeamSelect}
              />
              <SelectedTeamsList
                currentPersonnel={currentPersonnel}
                domainTeams={domainTeams}
                isTeamManagerAssignable={!!user?.meta?.isTeamManagerAssignable}
                onTeamSelectionDelete={onTeamSelectionDelete}
                onTeamSelectionRoleChange={onTeamSelectionRoleChange}
              />
            </div>
          </>
        )}
        <hr className='tw-mt-8 tw-h-px tw-border-0 tw-bg-grey-100' />
        <div className='form-container-section'>
          <PersonnelCustomFieldsForm
            currentPersonnel={currentPersonnel}
            onDateFieldChange={onDateFieldChange}
            onFieldOptionChange={onFieldOptionChange}
            onPersonnelFieldChange={onPersonnelFieldChange}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
          />
        </div>
      </div>
    </div>
  );
}

PersonnelForm.propTypes = {
  assignableCompanies: PropTypes.arrayOf(resourceShape('assignableCompany'))
    .isRequired,
  assignableRoles: PropTypes.arrayOf(resourceShape('companyRole')).isRequired,
  currentPersonnel: PropTypes.object,
  currentSubcontractor: PropTypes.object,
  domainLineManager: resourceShape('lineManager'),
  domainProfilePhoto: PropTypes.shape({
    croppedUrl: PropTypes.string,
    id: PropTypes.string,
    originalUrl: PropTypes.string,
  }),
  domainTeams: PropTypes.objectOf(resourceShape('team')),
  hasTeamPersonnelEditableAccess: PropTypes.bool,
  isCreatableCompanyRole: PropTypes.bool,
  isEditableProfileAccess: PropTypes.bool,
  isSubcontractor: PropTypes.bool,
  onAssignableRoleAdd: PropTypes.func,
  onDateFieldChange: PropTypes.func,
  onDivisionOptionChange: PropTypes.func,
  onFieldOptionChange: PropTypes.func,
  onNewCompanyRoleAdd: PropTypes.func,
  onPersonnelFieldChange: PropTypes.func,
  onPersonnelInputChange: PropTypes.func,
  onPersonnelOptionChange: PropTypes.func,
  onPhotoModalOpen: PropTypes.func,
  onSubcontractorSelect: PropTypes.func,
  onTeamSelect: PropTypes.func,
  onTeamSelectionDelete: PropTypes.func,
  onTeamSelectionRoleChange: PropTypes.func,
  onToggle: PropTypes.func,
  removeErrorStyling: PropTypes.func,
  requestError: PropTypes.object,
  setCurrentPersonnel: PropTypes.func,
  setIsSubcontractor: PropTypes.func,
  sidePanelContext: PropTypes.string,
  user: resourceShape('user'),
};
